import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { format } from 'date-fns';
import { useData } from '../contexts/DataContext';
import SafeIcon from '../common/SafeIcon';
import DeleteConfirmModal from '../components/DeleteConfirmModal';
import * as FiIcons from 'react-icons/fi';

const { FiSearch, FiFilter, FiUsers, FiPlus, FiCalendar, FiClock, FiPackage, FiUser, FiMail, FiMapPin, FiAlertTriangle, FiEdit, FiTrash2, FiArchive, FiRefreshCw, FiEye, FiEyeOff } = FiIcons;

const Members = () => {
  const { members, allMembers, loading, archiveMember, restoreMember, showArchived, toggleShowArchived } = useData();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterProgram, setFilterProgram] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [sortOption, setSortOption] = useState('name');
  const [archiveModalOpen, setArchiveModalOpen] = useState(false);
  const [restoreModalOpen, setRestoreModalOpen] = useState(false);
  const [memberToAction, setMemberToAction] = useState(null);

  const programs = [...new Set(allMembers.flatMap(member => member.programs || []))];

  // Filter and sort members
  const filteredMembers = members.filter(member => {
    const matchesSearch = member.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      member.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (member.city && member.city.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesProgram = !filterProgram || (member.programs && member.programs.includes(filterProgram));
    const matchesStatus = !filterStatus ||
      (filterStatus === 'needs_attention' && needsAttention(member)) ||
      (filterStatus === 'active' && !needsAttention(member));
    return matchesSearch && matchesProgram && matchesStatus;
  });

  const sortedMembers = [...filteredMembers].sort((a, b) => {
    switch (sortOption) {
      case 'name':
        return a.name.localeCompare(b.name);
      case 'recent':
        const dateA = a.last_session ? new Date(a.last_session) : new Date(0);
        const dateB = b.last_session ? new Date(b.last_session) : new Date(0);
        return dateB - dateA;
      case 'oldest':
        const joinDateA = a.date_joined ? new Date(a.date_joined) : new Date(0);
        const joinDateB = b.date_joined ? new Date(b.date_joined) : new Date(0);
        return joinDateA - joinDateB;
      case 'newest':
        const newestDateA = a.date_joined ? new Date(a.date_joined) : new Date(0);
        const newestDateB = b.date_joined ? new Date(b.date_joined) : new Date(0);
        return newestDateB - newestDateA;
      case 'needs_attention':
        // Sort by needs attention status first, then by last session date
        const needsAttentionA = needsAttention(a);
        const needsAttentionB = needsAttention(b);
        if (needsAttentionA && !needsAttentionB) return -1;
        if (!needsAttentionA && needsAttentionB) return 1;
        const lastSessionA = a.last_session ? new Date(a.last_session) : new Date(0);
        const lastSessionB = b.last_session ? new Date(b.last_session) : new Date(0);
        return lastSessionA - lastSessionB;
      default:
        return 0;
    }
  });

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return format(new Date(dateString), 'MMM d, yyyy');
  };

  // Check if a member needs attention (no sessions or last session > 30 days ago)
  const needsAttention = (member) => {
    if (!member.last_session) return true;
    const lastSession = new Date(member.last_session);
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    return lastSession < thirtyDaysAgo;
  };

  // Handle member archive
  const handleArchiveClick = (member) => {
    setMemberToAction(member);
    setArchiveModalOpen(true);
  };

  const handleConfirmArchive = async () => {
    if (memberToAction) {
      await archiveMember(memberToAction.id);
      setArchiveModalOpen(false);
      setMemberToAction(null);
    }
  };

  // Handle member restore
  const handleRestoreClick = (member) => {
    setMemberToAction(member);
    setRestoreModalOpen(true);
  };

  const handleConfirmRestore = async () => {
    if (memberToAction) {
      await restoreMember(memberToAction.id);
      setRestoreModalOpen(false);
      setMemberToAction(null);
    }
  };

  return (
    <div className="p-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">BPA Members</h1>
            <p className="text-gray-600">Manage and view all member profiles</p>
          </div>

          <div className="flex items-center space-x-3">
            <button
              onClick={toggleShowArchived}
              className="flex items-center space-x-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
            >
              <SafeIcon icon={showArchived ? FiEyeOff : FiEye} className="w-4 h-4" />
              <span>{showArchived ? "Hide Archived" : "Show Archived"}</span>
            </button>

            <Link
              to="/add-member"
              className="flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              <SafeIcon icon={FiPlus} className="w-4 h-4" />
              <span>Add New Member</span>
            </Link>
          </div>
        </div>
      </motion.div>

      {/* Filters */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6"
      >
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search members by name, email, or city..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
          </div>

          <div className="md:w-48">
            <div className="relative">
              <SafeIcon icon={FiFilter} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <select
                value={filterProgram}
                onChange={(e) => setFilterProgram(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent appearance-none"
              >
                <option value="">All Programs</option>
                {programs.map(program => (
                  <option key={program} value={program}>{program}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="md:w-48">
            <div className="relative">
              <SafeIcon icon={FiFilter} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent appearance-none"
              >
                <option value="">All Status</option>
                <option value="needs_attention">Needs Attention</option>
                <option value="active">Active</option>
              </select>
            </div>
          </div>

          <div className="md:w-48">
            <div className="relative">
              <SafeIcon icon={FiFilter} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <select
                value={sortOption}
                onChange={(e) => setSortOption(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent appearance-none"
              >
                <option value="name">Sort by Name</option>
                <option value="recent">Recent Sessions First</option>
                <option value="newest">Newest Members First</option>
                <option value="oldest">Oldest Members First</option>
                <option value="needs_attention">Needs Attention First</option>
              </select>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Members List */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        {loading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          </div>
        ) : sortedMembers.length > 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            {/* Desktop Table - Hidden on mobile */}
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <div className="flex items-center space-x-1">
                        <SafeIcon icon={FiUser} className="w-4 h-4" />
                        <span>Member</span>
                      </div>
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <div className="flex items-center space-x-1">
                        <SafeIcon icon={FiCalendar} className="w-4 h-4" />
                        <span>Joined</span>
                      </div>
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <div className="flex items-center space-x-1">
                        <SafeIcon icon={FiClock} className="w-4 h-4" />
                        <span>Last Session</span>
                      </div>
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <div className="flex items-center space-x-1">
                        <SafeIcon icon={FiPackage} className="w-4 h-4" />
                        <span>Programs</span>
                      </div>
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <div className="flex items-center space-x-1">
                        <SafeIcon icon={FiMapPin} className="w-4 h-4" />
                        <span>Location</span>
                      </div>
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {sortedMembers.map((member) => (
                    <tr
                      key={member.id}
                      className={`hover:bg-gray-50 ${member.is_archived ? 'bg-gray-100' : needsAttention(member) ? 'bg-red-50' : ''}`}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10 bg-primary-100 rounded-full flex items-center justify-center">
                            <SafeIcon icon={FiUser} className="h-5 w-5 text-primary-600" />
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {member.name}
                              {member.is_archived && (
                                <span className="ml-2 px-2 py-0.5 bg-gray-200 text-gray-700 text-xs rounded-full">
                                  Archived
                                </span>
                              )}
                            </div>
                            <div className="text-sm text-gray-500 flex items-center">
                              <SafeIcon icon={FiMail} className="h-3 w-3 mr-1" />
                              <a href={`mailto:${member.email}`} className="hover:text-primary-600 transition-colors">
                                {member.email}
                              </a>
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(member.date_joined)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className={`text-sm ${needsAttention(member) && !member.is_archived ? 'text-red-600 font-medium flex items-center' : 'text-gray-500'}`}>
                          {needsAttention(member) && !member.is_archived && (
                            <SafeIcon icon={FiAlertTriangle} className="w-4 h-4 mr-1 text-red-500" />
                          )}
                          {member.last_session ? formatDate(member.last_session) : 'Never'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex flex-wrap gap-1">
                          {member.programs && member.programs.map(program => (
                            <span key={program} className="px-2 py-0.5 bg-primary-50 text-primary-700 text-xs rounded-full">
                              {program}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {[member.city, member.state, member.country].filter(Boolean).join(', ') || 'Not specified'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          <Link to={`/members/${member.id}`} className="text-primary-600 hover:text-primary-900">
                            View
                          </Link>
                          {!member.is_archived && (
                            <>
                              <Link to={`/edit-member/${member.id}`} className="text-blue-600 hover:text-blue-900">
                                <SafeIcon icon={FiEdit} className="w-4 h-4" />
                              </Link>
                              <button
                                onClick={() => handleArchiveClick(member)}
                                className="text-amber-600 hover:text-amber-900"
                                title="Archive member"
                              >
                                <SafeIcon icon={FiArchive} className="w-4 h-4" />
                              </button>
                            </>
                          )}
                          {member.is_archived && (
                            <button
                              onClick={() => handleRestoreClick(member)}
                              className="text-green-600 hover:text-green-900"
                              title="Restore member"
                            >
                              <SafeIcon icon={FiRefreshCw} className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards - Visible only on mobile */}
            <div className="md:hidden divide-y divide-gray-200">
              {sortedMembers.map((member) => (
                <div 
                  key={member.id} 
                  className={`p-4 ${member.is_archived ? 'bg-gray-100' : needsAttention(member) ? 'bg-red-50' : ''}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center">
                      <div className="h-10 w-10 bg-primary-100 rounded-full flex items-center justify-center">
                        <SafeIcon icon={FiUser} className="h-5 w-5 text-primary-600" />
                      </div>
                      <div className="ml-3">
                        <div className="text-sm font-medium text-gray-900 flex items-center">
                          {member.name}
                          {member.is_archived && (
                            <span className="ml-2 px-2 py-0.5 bg-gray-200 text-gray-700 text-xs rounded-full">
                              Archived
                            </span>
                          )}
                          {needsAttention(member) && !member.is_archived && (
                            <span className="ml-2 flex items-center text-red-600">
                              <SafeIcon icon={FiAlertTriangle} className="w-3 h-3 mr-1" />
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex flex-shrink-0">
                      <Link 
                        to={`/members/${member.id}`} 
                        className="px-2 py-1 text-primary-600 hover:text-primary-900"
                      >
                        View
                      </Link>
                    </div>
                  </div>

                  <div className="mt-2 space-y-1 text-sm text-gray-500">
                    <div className="flex justify-between">
                      <span className="font-medium">Email:</span>
                      <a 
                        href={`mailto:${member.email}`} 
                        className="text-right truncate max-w-[65%] hover:text-primary-600 transition-colors"
                      >
                        {member.email}
                      </a>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-medium">Joined:</span>
                      <span>{formatDate(member.date_joined)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-medium">Last Session:</span>
                      <span className={`${needsAttention(member) && !member.is_archived ? 'text-red-600 font-medium' : ''}`}>
                        {member.last_session ? formatDate(member.last_session) : 'Never'}
                      </span>
                    </div>
                    {member.programs && member.programs.length > 0 && (
                      <div>
                        <span className="font-medium">Programs:</span>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {member.programs.map(program => (
                            <span key={program} className="px-2 py-0.5 bg-primary-50 text-primary-700 text-xs rounded-full">
                              {program}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {(member.city || member.state || member.country) && (
                      <div className="flex justify-between">
                        <span className="font-medium">Location:</span>
                        <span className="text-right">
                          {[member.city, member.state, member.country].filter(Boolean).join(', ')}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Action buttons in a separate row to ensure they fit */}
                  <div className="mt-3 pt-2 border-t border-gray-100 flex justify-end">
                    {!member.is_archived ? (
                      <div className="flex space-x-3">
                        <Link 
                          to={`/edit-member/${member.id}`} 
                          className="p-1 text-blue-600 hover:text-blue-900"
                        >
                          <SafeIcon icon={FiEdit} className="w-5 h-5" />
                        </Link>
                        <button
                          onClick={() => handleArchiveClick(member)}
                          className="p-1 text-amber-600 hover:text-amber-900"
                          title="Archive member"
                        >
                          <SafeIcon icon={FiArchive} className="w-5 h-5" />
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => handleRestoreClick(member)}
                        className="p-1 text-green-600 hover:text-green-900"
                        title="Restore member"
                      >
                        <SafeIcon icon={FiRefreshCw} className="w-5 h-5" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
            <SafeIcon icon={FiUsers} className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">
              {searchTerm || filterProgram || filterStatus
                ? 'No members match your filters'
                : showArchived
                  ? 'No archived members found'
                  : 'No active members found'}
            </p>
            {(searchTerm || filterProgram || filterStatus) && (
              <button
                onClick={() => {
                  setSearchTerm('');
                  setFilterProgram('');
                  setFilterStatus('');
                }}
                className="mt-4 px-4 py-2 text-primary-600 hover:text-primary-700 font-medium"
              >
                Clear filters
              </button>
            )}
          </div>
        )}
      </motion.div>

      {/* Archive Confirmation Modal */}
      <AnimatePresence>
        {archiveModalOpen && memberToAction && (
          <DeleteConfirmModal
            title="Archive Member"
            message={`Are you sure you want to archive ${memberToAction.name}? This will hide them from the active members list, but all their data and history will be preserved.`}
            confirmText="Archive"
            confirmIcon={FiArchive}
            confirmColor="bg-amber-600 hover:bg-amber-700"
            onConfirm={handleConfirmArchive}
            onCancel={() => {
              setArchiveModalOpen(false);
              setMemberToAction(null);
            }}
          />
        )}
      </AnimatePresence>

      {/* Restore Confirmation Modal */}
      <AnimatePresence>
        {restoreModalOpen && memberToAction && (
          <DeleteConfirmModal
            title="Restore Member"
            message={`Are you sure you want to restore ${memberToAction.name}? This will make them visible in the active members list again.`}
            confirmText="Restore"
            confirmIcon={FiRefreshCw}
            confirmColor="bg-green-600 hover:bg-green-700"
            onConfirm={handleConfirmRestore}
            onCancel={() => {
              setRestoreModalOpen(false);
              setMemberToAction(null);
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default Members;